'use client';

import { useAuthUser } from '@/hooks/useAuthUser';
import serviceWorkerManager from '@/lib/service-worker-registration';
import { isTouchDevice } from '@/utils/touchDevice';
import { Download, Share, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'pwa_install_banner_dismissed';

export default function PwaInstallBanner() {
  const t = useTranslations('PwaInstallBanner');
  const { isAuthenticated } = useAuthUser();
  const [isVisible, setIsVisible] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const [hasInstallPrompt, setHasInstallPrompt] = useState(false);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isTouchDevice()) return;
    if (serviceWorkerManager.isStandalone()) return;
    if (localStorage.getItem(STORAGE_KEY) === 'true') return;

    const isIos = serviceWorkerManager.isIOS();
    setShowIosInstructions(isIos);

    // On iOS, show banner immediately with share instructions
    if (isIos) {
      setIsVisible(true);
      return;
    }

    // On non-iOS, only show banner if the browser fires beforeinstallprompt
    // (Chrome, Brave, Edge, Samsung Internet support it; Opera, Firefox don't)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      // Respect user's dismiss state even if beforeinstallprompt fires again
      if (localStorage.getItem(STORAGE_KEY) === 'true') {
        return;
      }
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setHasInstallPrompt(true);
      setIsVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
    };
  }, [isAuthenticated]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPromptRef.current) {
      await deferredPromptRef.current.prompt();
      const { outcome } = await deferredPromptRef.current.userChoice;
      if (outcome === 'accepted') {
        handleDismiss();
      }
      deferredPromptRef.current = null;
      setHasInstallPrompt(false);
    }
  }, [handleDismiss]);

  if (!isVisible) return null;

  // Sticky (not fixed) so the banner participates in document flow and
  // doesn't hide footer content.
  return (
    // role='region' (not 'banner'): the WAI-ARIA banner landmark is reserved
    // for the site-level header; on a transient install-prompt widget it would
    // pollute screen-reader landmark navigation. A labelled region is the
    // accurate landmark (#5152).
    <div
      role='region'
      aria-label={t('landmark')}
      className='sticky bottom-0 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300'
    >
      {/* Dark background for prominent visibility - matches dark mode surface-card */}
      <div className='border-t border-[#2a2d35] bg-[#1a1d24] px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-4 shadow-[0_-4px_16px_rgba(0,0,0,0.4)]'>
        <div className='mx-auto flex max-w-lg items-center justify-center gap-3'>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src='/logo-without-name.png'
            alt='Lexomat'
            width={44}
            height={44}
            className='h-11 w-11 flex-shrink-0 rounded-xl'
          />

          <div className='min-w-0 flex-1'>
            <p className='text-sm font-semibold text-white'>{t('message')}</p>
            {/* White-alpha, not a semantic theme token: this sits on the same
                fixed-dark scrim as the dismiss control below (see its comment),
                so a theme-following token would flip dark-on-dark in light mode. */}
            <p className='mt-0.5 text-xs text-white/60'>
              {showIosInstructions ? (
                <span className='inline-flex items-center gap-1'>
                  <Share className='inline h-3 w-3' />
                  {t('iosInstructions')}
                </span>
              ) : (
                t('description')
              )}
            </p>
          </div>

          {!showIosInstructions && hasInstallPrompt && (
            <button
              type='button'
              onClick={handleInstall}
              className='flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-brand-tertiary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-tertiary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50'
            >
              <Download className='h-4 w-4' />
              {t('install')}
            </button>
          )}

          {/* White-alpha (not semantic theme tokens): the banner is a fixed-dark
              scrim in both light and dark mode, so the dismiss control must stay
              light-on-dark — theme-following tokens would flip to dark text/hover
              on a dark surface in light mode. */}
          <button
            type='button'
            onClick={handleDismiss}
            className='flex-shrink-0 rounded-lg p-3 text-white/60 transition-colors hover:bg-white/10 hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50'
            aria-label={t('dismiss')}
          >
            <X className='h-5 w-5' />
          </button>
        </div>
      </div>
    </div>
  );
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
